import { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Plus, Loader2, Trash2, ListChecks, Camera, TrendingUp, ChevronDown, Pencil, PackagePlus,
  CalendarRange, Wallet, Save, FileDown, Share2, Copy, Check, MessageCircle, X, RotateCcw,
  FileSpreadsheet, Download, Flag, GitCompare,
} from "lucide-react";
import { rupiah, rupiahShort, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const toDate = (d) => (d ? String(d).slice(0, 10) : "");
const iso = (d) => (d ? new Date(d).toISOString() : null);

export function ProgressTab({ project }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const [rabInput, setRabInput] = useState("");
  const [importing, setImporting] = useState(false);
  const rabFileRef = useRef();

  const importRab = async (e) => {
    const file = e.target.files?.[0];
    if (rabFileRef.current) rabFileRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post(`/projects/${project.id}/workitems/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Impor selesai: ${res.data.items} item · ${res.data.subs} sub item`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal impor Excel"); } finally { setImporting(false); }
  };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/rab-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "Template-RAB.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Gagal unduh template"); }
  };

  const [compareOpen, setCompareOpen] = useState(false);
  const saveBaseline = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/rab-baseline`);
      toast.success("Baseline RAB disimpan");
      await load();
    } catch { toast.error("Gagal menyimpan baseline"); } finally { setBusy(false); }
  };
  const deleteBaseline = async () => {
    try {
      await api.delete(`/projects/${project.id}/rab-baseline`);
      toast.success("Baseline dihapus");
      setCompareOpen(false);
      await load();
    } catch { toast.error("Gagal hapus baseline"); }
  };
  const buildComparison = () => {
    const b = data?.rabBaseline;
    if (!b) return [];
    const curItems = data.items || [];
    const curMap = new Map(curItems.map((i) => [i.id, i]));
    const bIds = new Set((b.items || []).map((i) => i.id));
    const rows = [];
    (b.items || []).forEach((bi) => {
      const cur = curMap.get(bi.id);
      const currentValue = cur ? cur.nilai : 0;
      rows.push({ name: bi.name, baselineValue: bi.value, currentValue, delta: currentValue - bi.value, status: cur ? (currentValue !== bi.value ? "changed" : "same") : "removed" });
    });
    curItems.forEach((ci) => { if (!bIds.has(ci.id)) rows.push({ name: ci.name, baselineValue: 0, currentValue: ci.nilai, delta: ci.nilai, status: "new" }); });
    return rows;
  };

  const [itemOpen, setItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ id: null, name: "", nilai: "", startDate: "", endDate: "" });

  const [subOpen, setSubOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [portalUrl, setPortalUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const openShare = async () => {
    setSharing(true);
    try {
      const res = await api.get(`/projects/${project.id}/portal-link`);
      setPortalUrl(`${window.location.origin}/api/public/portal/${res.data.slug}/share`);
      setCopied(false);
      setShareOpen(true);
    } catch { toast.error("Gagal membuat link portal"); } finally { setSharing(false); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(portalUrl); setCopied(true); toast.success("Link disalin"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Gagal menyalin"); }
  };
  const shareWhatsApp = () => {
    const msg = `Halo, berikut link untuk memantau progress pekerjaan proyek *${project.name}* secara realtime:\n\n${portalUrl}\n\nTerima kasih.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const resetLink = async () => {
    setSharing(true);
    try {
      const res = await api.post(`/projects/${project.id}/portal-link/reset`);
      setPortalUrl(`${window.location.origin}/api/public/portal/${res.data.slug}/share`);
      setCopied(false);
      toast.success("Link lama diputus. Link baru dibuat.");
    } catch { toast.error("Gagal reset link"); } finally { setSharing(false); }
  };

  const exportProgressPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await api.get(`/projects/${project.id}/progress/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Laporan-Progress-${project.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Laporan Progress PDF diunduh");
    } catch { toast.error("Gagal export laporan progress"); } finally { setExportingPdf(false); }
  };
  const [subForm, setSubForm] = useState({ id: null, name: "", harga: "" });
  const [subParent, setSubParent] = useState(null);

  const [logOpen, setLogOpen] = useState(false);
  const [logTarget, setLogTarget] = useState(null); // {workItemId, subItemId, title, current}
  const [logForm, setLogForm] = useState({ progress: 0, notes: "", date: "" });
  const [editingId, setEditingId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [entries, setEntries] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const res = await api.get(`/projects/${project.id}/progress-summary`);
      setData(res.data);
      setRabInput(res.data.rabTotal ? String(res.data.rabTotal) : "");
    } catch { toast.error("Gagal memuat progress"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const saveRab = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/rab-total`, { rabTotal: parseInt(rabInput || 0, 10) });
      toast.success("Total RAB disimpan");
      await load();
    } catch { toast.error("Gagal menyimpan RAB"); } finally { setBusy(false); }
  };

  // Work item add/edit
  const openAddItem = () => { setItemForm({ id: null, name: "", nilai: "", startDate: "", endDate: "" }); setItemOpen(true); };
  const openEditItem = (it) => { setItemForm({ id: it.id, name: it.name, nilai: it.hasSubs ? "" : String(it.manualNilai || ""), startDate: toDate(it.startDate), endDate: toDate(it.endDate) }); setItemOpen(true); };
  const saveItem = async () => {
    if (!itemForm.name) return toast.error("Nama item wajib diisi");
    setBusy(true);
    try {
      const payload = { name: itemForm.name, nilai: parseInt(itemForm.nilai || 0, 10), startDate: iso(itemForm.startDate), endDate: iso(itemForm.endDate) };
      if (itemForm.id) { await api.put(`/workitems/${itemForm.id}`, payload); toast.success("Item diperbarui"); }
      else { await api.post(`/projects/${project.id}/workitems`, payload); toast.success("Item ditambahkan"); }
      setItemOpen(false); await load();
    } catch { toast.error("Gagal menyimpan item"); } finally { setBusy(false); }
  };
  const removeItem = async (id) => {
    try { await api.delete(`/workitems/${id}`); toast.success("Item dihapus"); await load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  // Sub item add/edit
  const openAddSub = (item) => { setSubParent(item); setSubForm({ id: null, name: "", harga: "" }); setSubOpen(true); };
  const openEditSub = (item, sub) => { setSubParent(item); setSubForm({ id: sub.id, name: sub.name, harga: String(sub.harga || "") }); setSubOpen(true); };
  const saveSub = async () => {
    if (!subForm.name) return toast.error("Nama sub item wajib diisi");
    setBusy(true);
    try {
      const payload = { name: subForm.name, harga: parseInt(subForm.harga || 0, 10) };
      if (subForm.id) { await api.put(`/subitems/${subForm.id}`, payload); toast.success("Sub item diperbarui"); }
      else { await api.post(`/workitems/${subParent.id}/subitems`, payload); toast.success("Sub item ditambahkan"); }
      setSubOpen(false); await load();
    } catch { toast.error("Gagal menyimpan sub item"); } finally { setBusy(false); }
  };
  const deleteSub = async (sub) => {
    try { await api.delete(`/subitems/${sub.id}`); toast.success("Sub item dihapus"); await load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  // Progress log (photo) — target = work item (no subs) or sub item
  const openLog = async (target) => {
    setLogTarget(target);
    setLogForm({ progress: target.current || 0, notes: "", date: "" });
    setEditingId(null);
    setPhotos([]); setEntries([]);
    setLogOpen(true);
    try {
      const params = target.subItemId ? { subItemId: target.subItemId } : {};
      const res = await api.get(`/workitems/${target.workItemId}/progress`, { params });
      setEntries(res.data);
    } catch { setEntries([]); }
  };
  const MAX_PHOTOS = 10;
  const uploadPhoto = async (e) => {
    let files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) { toast.error(`Maksimal ${MAX_PHOTOS} foto`); return; }
    if (files.length > remaining) { toast.warning(`Hanya ${remaining} foto ditambahkan (maks ${MAX_PHOTOS})`); files = files.slice(0, remaining); }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, res.data.path]);
      }
      toast.success("Foto terunggah");
    } catch { toast.error("Gagal upload"); } finally { setUploading(false); }
  };
  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));
  const submitLog = async () => {
    setBusy(true);
    try {
      const payload = { progress: logForm.progress, notes: logForm.notes, date: iso(logForm.date), photoUrls: photos, subItemId: logTarget.subItemId || null };
      if (editingId) {
        await api.put(`/progress/${editingId}`, payload);
        toast.success("Progress diperbarui");
      } else {
        await api.post(`/workitems/${logTarget.workItemId}/progress`, payload);
        toast.success("Progress dicatat");
      }
      setEditingId(null);
      setLogForm({ progress: logTarget.current || 0, notes: "", date: "" });
      setPhotos([]);
      await refreshEntries();
      await load();
    } catch { toast.error("Gagal menyimpan progress"); } finally { setBusy(false); }
  };
  const refreshEntries = async () => {
    if (!logTarget) return;
    try {
      const params = logTarget.subItemId ? { subItemId: logTarget.subItemId } : {};
      const res = await api.get(`/workitems/${logTarget.workItemId}/progress`, { params });
      setEntries(res.data);
    } catch { /* ignore */ }
  };
  const startEdit = (e) => {
    setEditingId(e.id);
    setLogForm({ progress: e.progress, notes: e.notes || "", date: toDate(e.date) });
    setPhotos(e.photoUrls || []);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setLogForm({ progress: logTarget?.current || 0, notes: "", date: "" });
    setPhotos([]);
  };
  const deleteEntry = async (id) => {
    try {
      await api.delete(`/progress/${id}`);
      toast.success("Entri progress dihapus");
      if (editingId === id) cancelEdit();
      await refreshEntries();
      await load();
    } catch { toast.error("Gagal menghapus entri"); }
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-amber-600" /></div>;

  const items = data?.items || [];
  const curve = data?.curve || [];
  const actual = data?.totalProgress || 0;
  const planned = data?.plannedProgress || 0;
  const deviation = +(actual - planned).toFixed(1);
  const rabUsed = data?.rab || 0;
  const rabMismatch = (data?.rabTotal || 0) > 0 && data.rabTotal !== data.totalItemValue;

  // Schedule bars range
  const schedStart = data?.start ? new Date(data.start).getTime() : 0;
  const schedEnd = data?.end ? new Date(data.end).getTime() : 0;
  const schedSpan = Math.max(schedEnd - schedStart, 1);
  const barPos = (it) => {
    if (!it.startDate || !it.endDate || !schedStart) return null;
    const s = new Date(it.startDate).getTime();
    const e = new Date(it.endDate).getTime();
    const left = Math.max(0, ((s - schedStart) / schedSpan) * 100);
    const width = Math.max(2, ((e - s) / schedSpan) * 100);
    return { left: `${left}%`, width: `${Math.min(100 - left, width)}%` };
  };

  return (
    <div>
      {/* RAB input */}
      <Card className="p-4 border-slate-200 bg-white mb-4">
        <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-amber-600" /><span className="font-semibold text-sm text-slate-900">Total Nilai RAB Pekerjaan</span></div>
        <p className="text-xs text-slate-500 mb-3">Dipakai sebagai dasar perhitungan bobot (%). <b>Terpisah dari nilai proyek di Cash Flow</b> karena RAB sering dinegosiasi klien. Kosongkan untuk memakai total nilai item otomatis.</p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input data-testid="rab-total-input" type="number" value={rabInput} onChange={(e) => setRabInput(e.target.value)} placeholder="mis. 250000000" className="font-mono max-w-xs" />
          <Button data-testid="rab-save-btn" onClick={saveRab} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"><Save className="w-4 h-4" /> Simpan RAB</Button>
          <div className="text-xs text-slate-500 sm:ml-auto">
            Dasar bobot: <b className="font-mono text-slate-800">{rupiah(rabUsed)}</b> · Total item: <b className="font-mono text-slate-800">{rupiah(data?.totalItemValue || 0)}</b>
          </div>
        </div>
        {rabMismatch && <p className="text-[11px] text-amber-700 mt-2">⚠ Total item ({rupiahShort(data.totalItemValue)}) belum sama dengan RAB ({rupiahShort(data.rabTotal)}). Bobot total tidak akan mencapai 100%.</p>}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
          <span className="text-xs text-slate-500 mr-auto">Isi item & sub item otomatis dari file Excel:</span>
          <input ref={rabFileRef} type="file" accept=".xlsx" hidden onChange={importRab} />
          <Button data-testid="btn-rab-template" size="sm" variant="ghost" onClick={downloadTemplate} className="h-8 gap-1.5 text-xs text-slate-600"><Download className="w-3.5 h-3.5" /> Unduh Template</Button>
          <Button data-testid="btn-import-rab" size="sm" variant="outline" onClick={() => rabFileRef.current?.click()} disabled={importing} className="h-8 gap-1.5 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Impor Excel
          </Button>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          {!data.rabBaseline ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 mr-auto">Simpan RAB awal sebagai <b>baseline</b> untuk melacak deviasi setelah negosiasi.</span>
              <Button data-testid="btn-save-baseline" size="sm" variant="outline" onClick={saveBaseline} disabled={busy || !(data.items || []).length} className="h-8 gap-1.5 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"><Flag className="w-3.5 h-3.5" /> Simpan Baseline</Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">Baseline · {fmtDate(data.rabBaseline.savedAt)}</span>
              <div className="text-xs text-slate-600">
                {rupiahShort(data.rabBaseline.totalItemValue)} → <b className="font-mono">{rupiahShort(data.totalItemValue)}</b>
                {(() => { const dv = (data.totalItemValue || 0) - (data.rabBaseline.totalItemValue || 0); const pct = data.rabBaseline.totalItemValue ? (dv / data.rabBaseline.totalItemValue * 100) : 0; return <span data-testid="baseline-deviation" className={`ml-1 font-mono font-semibold ${dv > 0 ? "text-red-600" : dv < 0 ? "text-green-600" : "text-slate-500"}`}>({dv >= 0 ? "+" : ""}{rupiahShort(dv)} · {dv >= 0 ? "+" : ""}{pct.toFixed(1)}%)</span>; })()}
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <Button data-testid="btn-view-comparison" size="sm" variant="outline" onClick={() => setCompareOpen(true)} className="h-8 gap-1.5 text-xs"><GitCompare className="w-3.5 h-3.5" /> Rincian Deviasi</Button>
                <Button data-testid="btn-update-baseline" size="sm" variant="ghost" onClick={saveBaseline} disabled={busy} className="h-8 gap-1.5 text-xs text-slate-500">Perbarui</Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="p-4 border-slate-200 bg-white"><div className="text-xs text-slate-500">Realisasi (Aktual)</div><div className="font-mono font-bold text-2xl text-amber-700">{actual.toFixed(1)}%</div></Card>
        <Card className="p-4 border-slate-200 bg-white"><div className="text-xs text-slate-500">Rencana (s/d hari ini)</div><div className="font-mono font-bold text-2xl text-blue-600">{planned.toFixed(1)}%</div></Card>
        <Card className="p-4 border-slate-200 bg-white"><div className="text-xs text-slate-500">Deviasi</div><div className="font-mono font-bold text-2xl" style={{ color: deviation >= 0 ? "#16a34a" : "#dc2626" }}>{deviation >= 0 ? "+" : ""}{deviation}%</div></Card>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-lg text-slate-900">Item Pekerjaan (RAB)</h3>
        <div className="flex gap-2">
          <Button data-testid="btn-share-portal" size="sm" variant="outline" onClick={openShare} disabled={sharing} className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Portal Klien
          </Button>
          <Button data-testid="btn-progress-pdf" size="sm" variant="outline" onClick={exportProgressPdf} disabled={exportingPdf} className="gap-1.5">
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Laporan Progress PDF
          </Button>
          <Button data-testid="btn-add-workitem" size="sm" onClick={openAddItem} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Item</Button>
        </div>
      </div>

      {/* S-Curve */}
      <Card className="p-4 border-slate-200 bg-white mb-4">
        <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-amber-600" /><span className="font-semibold text-sm text-slate-900">Kurva-S (Rencana vs Realisasi)</span></div>
        {curve.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-sm text-slate-400 text-center px-4">Belum ada data. Tambah item + jadwal (Time Schedule) dan catat progress untuk membentuk kurva.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={curve} margin={{ left: -10, right: 8, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d).slice(0, 6)} tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} unit="%" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="planned" name="Rencana (%)" stroke="#2563eb" strokeDasharray="4 4" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="actual" name="Realisasi (%)" stroke="#D97706" strokeWidth={3} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Time Schedule */}
      {items.length > 0 && (
        <Card className="p-4 border-slate-200 bg-white mb-4">
          <div className="flex items-center gap-2 mb-3"><CalendarRange className="w-4 h-4 text-amber-600" /><span className="font-semibold text-sm text-slate-900">Time Schedule</span><span className="text-[11px] text-slate-400 ml-auto">{fmtDate(data?.start)} – {fmtDate(data?.end)}</span></div>
          <div className="space-y-2">
            {items.map((it) => {
              const pos = barPos(it);
              return (
                <div key={it.id} className="flex items-center gap-3">
                  <div className="w-28 sm:w-40 shrink-0 text-xs text-slate-600 truncate">{it.name}</div>
                  <div className="flex-1 h-5 rounded bg-slate-100 relative overflow-hidden">
                    {pos ? (
                      <div className="absolute top-0 h-full rounded bg-blue-200" style={pos}>
                        <div className="h-full rounded bg-amber-500" style={{ width: `${it.lastProgress}%` }} />
                      </div>
                    ) : <span className="absolute inset-0 flex items-center pl-2 text-[10px] text-slate-400">belum dijadwalkan</span>}
                  </div>
                  <div className="w-10 text-right text-xs font-mono text-slate-600 shrink-0">{Number(it.lastProgress).toFixed(0)}%</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Items list */}
      {items.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 bg-white/50">
          <ListChecks className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Belum ada item pekerjaan. Tambahkan item + jadwal, lalu rincian Sub Item (RAB).</p>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => {
            const isOpen = !!expanded[it.id];
            const subs = it.subItems || [];
            return (
              <Card data-testid={`workitem-card-${it.id}`} key={it.id} className="border-slate-200 bg-white overflow-hidden">
                <div className="p-4 flex items-center gap-2">
                  <button data-testid={`workitem-toggle-${it.id}`} onClick={() => toggleExpand(it.id)} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                      <span className="font-semibold text-slate-900 truncate">{it.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-mono shrink-0">Bobot {it.weight.toFixed(1)}%</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 pl-6">{rupiah(it.nilai)} · {it.hasSubs ? `${it.subCount} sub item` : "tanpa sub item"} {it.startDate ? `· ${fmtDate(it.startDate)}→${fmtDate(it.endDate)}` : ""}</div>
                    <div className="pl-6"><Progress value={it.lastProgress} className="h-1.5 mt-2" /></div>
                  </button>
                  <div className="text-right shrink-0 w-12"><div className="font-mono font-bold text-lg text-slate-900">{Number(it.lastProgress).toFixed(0)}%</div></div>
                  {!it.hasSubs && (
                    <button data-testid={`workitem-log-${it.id}`} onClick={() => openLog({ workItemId: it.id, subItemId: null, title: it.name, current: it.lastProgress })} title="Foto & Progress" className="text-slate-300 hover:text-amber-600 shrink-0"><Camera className="w-4 h-4" /></button>
                  )}
                  <button data-testid={`edit-workitem-${it.id}`} onClick={() => openEditItem(it)} className="text-slate-300 hover:text-amber-600 shrink-0"><Pencil className="w-4 h-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button data-testid={`delete-workitem-${it.id}`} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent className="bg-white">
                      <AlertDialogHeader><AlertDialogTitle>Hapus item pekerjaan?</AlertDialogTitle><AlertDialogDescription>Seluruh sub item & log progress item ini akan dihapus.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => removeItem(it.id)} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500">Sub Item (rincian RAB)</span>
                      <Button data-testid={`btn-add-subitem-${it.id}`} size="sm" variant="outline" onClick={() => openAddSub(it)} className="h-7 gap-1 text-xs"><PackagePlus className="w-3.5 h-3.5" /> Sub Item</Button>
                    </div>
                    {subs.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">Belum ada sub item. Bila item ini tanpa sub item, catat progress & foto lewat ikon kamera. Tambah sub item untuk rincian per pekerjaan.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {subs.map((s) => (
                          <div data-testid={`subitem-row-${s.id}`} key={s.id} className="flex items-center gap-2.5 bg-white rounded-lg border border-slate-200 px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-slate-800 truncate">{s.name}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-mono shrink-0">{s.weight.toFixed(1)}%</span>
                              </div>
                              <div className="text-[11px] text-slate-500 font-mono">{rupiah(s.harga)}</div>
                              <Progress value={s.lastProgress} className="h-1 mt-1.5" />
                            </div>
                            <div className="font-mono font-bold text-sm text-slate-900 w-10 text-right shrink-0">{s.lastProgress}%</div>
                            <button data-testid={`subitem-log-${s.id}`} onClick={() => openLog({ workItemId: it.id, subItemId: s.id, title: `${it.name} › ${s.name}`, current: s.lastProgress })} title="Foto & Progress" className="text-slate-300 hover:text-amber-600 shrink-0"><Camera className="w-4 h-4" /></button>
                            <button data-testid={`edit-subitem-${s.id}`} onClick={() => openEditSub(it, s)} className="text-slate-300 hover:text-amber-600 shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><button data-testid={`delete-subitem-${s.id}`} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button></AlertDialogTrigger>
                              <AlertDialogContent className="bg-white">
                                <AlertDialogHeader><AlertDialogTitle>Hapus sub item?</AlertDialogTitle><AlertDialogDescription>Log progress & foto sub item ini akan dihapus.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => deleteSub(s)} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ))}
                        <div className="text-[11px] text-slate-500 pt-1">Total RAB item: <b className="font-mono text-slate-700">{rupiah(it.subTotal)}</b> · Terpasang: <b className="font-mono text-green-600">{rupiah(it.doneValue)}</b></div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Work item dialog */}
      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-lg">{itemForm.id ? "Edit Item Pekerjaan" : "Tambah Item Pekerjaan"}</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div><Label>Nama Item</Label><Input data-testid="workitem-name-input" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="Pekerjaan Plafon Gypsum" className="mt-1" /></div>
            <div><Label>Nilai Item (Rp) — opsional</Label><Input data-testid="workitem-nilai-input" type="number" value={itemForm.nilai} onChange={(e) => setItemForm({ ...itemForm, nilai: e.target.value })} placeholder="0" className="mt-1 font-mono" /><p className="text-[11px] text-slate-400 mt-1">Otomatis dihitung dari total Sub Item. Isi manual bila item tanpa rincian sub item.</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mulai</Label><Input data-testid="workitem-start-input" type="date" value={itemForm.startDate} onChange={(e) => setItemForm({ ...itemForm, startDate: e.target.value })} className="mt-1" /></div>
              <div><Label>Selesai</Label><Input data-testid="workitem-end-input" type="date" value={itemForm.endDate} onChange={(e) => setItemForm({ ...itemForm, endDate: e.target.value })} className="mt-1" /></div>
            </div>
            <p className="text-[11px] text-slate-400">Jadwal dipakai untuk garis Rencana pada Kurva-S & Time Schedule.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemOpen(false)}>Batal</Button>
            <Button data-testid="workitem-submit-button" onClick={saveItem} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub item dialog */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-lg">{subForm.id ? "Edit Sub Item" : "Tambah Sub Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div><Label>Nama Sub Item</Label><Input data-testid="subitem-name-input" value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} placeholder="Rangka Hollow 4x4" className="mt-1" /></div>
            <div><Label>Harga (Rp)</Label><Input data-testid="subitem-harga-input" type="number" value={subForm.harga} onChange={(e) => setSubForm({ ...subForm, harga: e.target.value })} placeholder="12000000" className="mt-1 font-mono" /><p className="text-[11px] text-slate-400 mt-1">Bobot % dihitung otomatis dari Total RAB. Progress dicatat lewat ikon kamera.</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubOpen(false)}>Batal</Button>
            <Button data-testid="subitem-submit-button" onClick={saveSub} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress/photo log dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto pf-scrollbar">
          <DialogHeader><DialogTitle className="font-display text-lg">{logTarget?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editingId && (
              <div className="flex items-center justify-between text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="edit-mode-banner">
                <span className="text-amber-700 flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Mode edit — perbarui data lalu simpan</span>
                <button data-testid="cancel-edit-btn" onClick={cancelEdit} className="text-amber-700 font-medium underline">Batal</button>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between"><Label>Progress</Label><span className="font-mono font-bold text-amber-700">{logForm.progress}%</span></div>
              <Slider data-testid="log-progress-slider" value={[logForm.progress]} onValueChange={(v) => setLogForm({ ...logForm, progress: v[0] })} max={100} step={5} className="mt-3" />
            </div>
            <div><Label>Tanggal</Label><Input data-testid="log-date-input" type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} className="mt-1" /></div>
            <div><Label>Catatan</Label><Textarea data-testid="log-notes-input" value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} placeholder="Progress lapangan hari ini…" className="mt-1 resize-none" rows={2} /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Foto Dokumentasi</Label>
                <span data-testid="photo-count" className="text-[11px] text-slate-400 font-mono">{photos.length}/{MAX_PHOTOS}</span>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={uploadPhoto} />
              <Button data-testid="log-upload-btn" type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || photos.length >= MAX_PHOTOS} className="mt-1 w-full gap-2">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {photos.length >= MAX_PHOTOS ? "Maksimal 10 foto" : "Upload Foto"}</Button>
              {photos.length > 0 && <div className="grid grid-cols-3 gap-2 mt-2">{photos.map((p, i) => (
                <div key={i} className="relative group">
                  <img src={fileUrl(p)} alt="" className="w-full h-16 object-cover rounded-md border border-slate-200" />
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                </div>
              ))}</div>}
            </div>
            <Button data-testid="log-submit-button" onClick={submitLog} disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? "Perbarui Progress" : "Simpan Progress")}</Button>

            {entries.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-500 mb-2">Riwayat Update <span className="font-normal text-slate-400">· arahkan untuk edit/hapus</span></div>
                <div className="space-y-1">
                  {entries.slice().reverse().map((e) => (
                    <div key={e.id} className={`flex gap-3 text-sm rounded-lg px-2 py-1.5 -mx-2 transition-colors ${editingId === e.id ? "bg-amber-50" : "hover:bg-slate-50"}`} data-testid={`entry-${e.id}`}>
                      <div className="font-mono font-bold text-amber-700 w-12 shrink-0">{e.progress}%</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-700">{e.notes || "-"}</div>
                        <div className="text-[11px] text-slate-400">{fmtDate(e.date)}</div>
                        {e.photoUrls?.length > 0 && <div className="flex gap-1.5 mt-1 flex-wrap">{e.photoUrls.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="w-12 h-12 object-cover rounded border border-slate-200" />)}</div>}
                      </div>
                      <div className="flex items-start gap-0.5 shrink-0">
                        <button data-testid={`edit-entry-${e.id}`} onClick={() => startEdit(e)} className="p-1.5 text-slate-400 hover:text-amber-600 rounded-md hover:bg-white" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-entry-${e.id}`} onClick={() => deleteEntry(e.id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded-md hover:bg-white" title="Hapus"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Baseline vs Revisi comparison dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><GitCompare className="w-5 h-5 text-indigo-600" /> Baseline vs Revisi RAB</DialogTitle></DialogHeader>
          {data?.rabBaseline && (() => {
            const rows = buildComparison();
            const bTot = data.rabBaseline.totalItemValue || 0, cTot = data.totalItemValue || 0, dTot = cTot - bTot;
            return (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-500">Baseline disimpan {fmtDate(data.rabBaseline.savedAt)}. Perbandingan nilai tiap item pekerjaan terhadap RAB awal.</p>
                <div className="max-h-[55vh] overflow-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0"><tr>
                      <th className="text-left font-medium px-3 py-2">Item Pekerjaan</th>
                      <th className="text-right font-medium px-3 py-2">Baseline</th>
                      <th className="text-right font-medium px-3 py-2">Revisi</th>
                      <th className="text-right font-medium px-3 py-2">Selisih</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((r, i) => (
                        <tr key={i} data-testid={`comparison-row-${i}`}>
                          <td className="px-3 py-2">{r.name}
                            {r.status === "new" && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 align-middle">BARU</span>}
                            {r.status === "removed" && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 align-middle">DIHAPUS</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-slate-500">{r.baselineValue ? rupiahShort(r.baselineValue) : "—"}</td>
                          <td className="px-3 py-2 text-right font-mono text-slate-800">{r.currentValue ? rupiahShort(r.currentValue) : "—"}</td>
                          <td className={`px-3 py-2 text-right font-mono ${r.delta > 0 ? "text-red-600" : r.delta < 0 ? "text-green-600" : "text-slate-400"}`}>{r.delta === 0 ? "—" : `${r.delta > 0 ? "+" : ""}${rupiahShort(r.delta)}`}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 font-semibold sticky bottom-0"><tr>
                      <td className="px-3 py-2">Total RAB</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{rupiahShort(bTot)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-900">{rupiahShort(cTot)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${dTot > 0 ? "text-red-600" : dTot < 0 ? "text-green-600" : "text-slate-500"}`}>{dTot >= 0 ? "+" : ""}{rupiahShort(dTot)}</td>
                    </tr></tfoot>
                  </table>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <Button data-testid="btn-delete-baseline" variant="ghost" size="sm" onClick={deleteBaseline} className="gap-1.5 text-red-500 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /> Hapus Baseline</Button>
                  <Button data-testid="btn-update-baseline-dialog" size="sm" onClick={saveBaseline} disabled={busy} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white"><Flag className="w-4 h-4" /> Jadikan Baseline Baru</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Client portal share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><Share2 className="w-5 h-5 text-amber-600" /> Bagikan Portal Klien</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Klien dapat memantau progress pekerjaan, kurva-S, dan foto dokumentasi secara realtime lewat link ini — tanpa perlu login. Data biaya & RAB tetap tersembunyi.</p>
            <div className="flex items-center gap-2">
              <Input data-testid="portal-url-input" readOnly value={portalUrl} className="font-mono text-xs bg-slate-50" onFocus={(e) => e.target.select()} />
              <Button data-testid="portal-copy-btn" type="button" variant="outline" size="icon" onClick={copyLink} className="shrink-0">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <Button data-testid="portal-whatsapp-btn" onClick={shareWhatsApp} className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white">
              <MessageCircle className="w-4 h-4" /> Bagikan ke WhatsApp
            </Button>
            <div className="pt-2 border-t border-slate-100">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="portal-reset-btn" disabled={sharing} className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1.5 disabled:opacity-50">
                    <RotateCcw className="w-3.5 h-3.5" /> Putuskan link lama & buat link baru
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset link portal?</AlertDialogTitle>
                    <AlertDialogDescription>Link yang sudah dibagikan sebelumnya akan langsung tidak berfungsi dan klien tidak bisa mengaksesnya lagi. Sebuah link baru akan dibuat untuk dibagikan ulang.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Batal</AlertDialogCancel>
                    <AlertDialogAction data-testid="portal-reset-confirm" onClick={resetLink} className="bg-red-600 hover:bg-red-700">Ya, Putuskan Link</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
